// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
import { z } from 'zod';
import { CaseStatus, SubStatus, ClosureReason, CaseSource } from '../constants/index.js';
import { MAX_LIST_PAGE_SIZE } from '../constants/pagination.js';
const allStatuses = Object.values(CaseStatus);
const allSubStatuses = Object.values(SubStatus);
const allClosureReasons = Object.values(ClosureReason);
const allSources = Object.values(CaseSource);
// Sprint 43.0.0 — Rights Request workflow completion enums
const rejectionReasonCodes = [
    'identity_not_verified',
    'manifestly_unfounded',
    'repetitive',
    'exempt',
    'out_of_scope',
    'duplicate',
];
const responseDeliveryMethods = ['email', 'portal', 'postal', 'in_person'];
const assignedDepartmentSchema = z
    .object({
    id: z.string().min(1),
    name: z.string().min(1),
})
    .nullable();
/**
 * Validation schema for updating a case (PATCH).
 * See: docs/backend/api-contracts.md - PATCH /api/v1/cases/:case_id
 */
export const updateCaseSchema = z
    .object({
    status: z.enum(allStatuses).optional(),
    sub_status: z.enum(allSubStatuses).nullable().optional(),
    assigned_to: z.string().nullable().optional(),
    description: z.string().max(10000).nullable().optional(),
    closure_summary: z.string().max(10000).nullable().optional(),
    closure_reason: z.enum(allClosureReasons).nullable().optional(),
    // Sprint 43.0.0 — Rights Request workflow completion fields
    assigned_department: assignedDepartmentSchema.optional(),
    extension_reason: z.string().max(2000).nullable().optional(),
    extension_notified_at: z.string().datetime().nullable().optional(),
    rejection_reason_code: z.enum(rejectionReasonCodes).nullable().optional(),
    rejection_reason_description: z.string().max(2000).nullable().optional(),
    response_delivery_method: z.enum(responseDeliveryMethods).nullable().optional(),
    response_delivered_at: z.string().datetime().nullable().optional(),
    // Sprint 43.0.0 — pause_reason is required when transitioning into a
    // SLA-paused (waiting_*) status; service-layer validation enforces this.
    pause_reason: z
        .enum(['awaiting_subject', 'awaiting_verification', 'third_party_dependency', 'other'])
        .optional(),
})
    .refine((data) => Object.keys(data).length > 0, {
    message: 'At least one field must be provided for update',
});
/**
 * fix-sprint #447 W9 (owner ruling OR3) — `PUT /cases/:id/third-party-involvement`.
 *
 * A route of its own rather than a key on `updateCaseSchema`: the change
 * re-dates the case from receipt against the installed deadline record whose
 * `coordination_mode` matches, and that must not ride on a PATCH that may also
 * move the status (a resume moves the same due date in the same request).
 */
export const setThirdPartyInvolvementSchema = z
    .object({
    third_party_involved: z.boolean(),
})
    .strict();
/**
 * Validation schema for adding a note to a case.
 */
export const addNoteSchema = z.object({
    content: z.string().min(1, 'Note content is required').max(5000),
    visibility: z.enum(['internal']).default('internal'),
    note_type: z
        .enum(['comment', 'status_change', 'sla_event', 'verification_event', 'system'])
        .default('comment'),
});
/**
 * Query parameter validation for case list endpoint.
 */
export const caseListQuerySchema = z.object({
    status: z.string().optional(),
    sub_status: z.string().optional(),
    request_type: z.string().optional(),
    assigned_to: z.string().optional(),
    business_unit_id: z.string().optional(),
    sla_breached: z.enum(['true', 'false']).optional(),
    /*
     * fix-sprint #431 — the list page's Source filter. It sent `source` and this
     * schema did not carry it, so zod stripped it and the list came back
     * unfiltered under a filter the page said was applied. One source or a
     * comma-separated list, each a `CaseSource` value; anything else is refused
     * rather than silently matching nothing.
     */
    source: z
        .string()
        .max(500)
        .refine((value) => value.split(',').every((part) => allSources.includes(part)), {
        message: 'Unknown case source',
    })
        .optional(),
    search: z.string().max(200).optional(),
    created_after: z.string().datetime().optional(),
    created_before: z.string().datetime().optional(),
    page: z.coerce.number().int().min(1).default(1),
    /*
     * The ceiling is the shared one, like every other route cap in the tree.
     *
     * This read `.max(100)` — a route cap below `MAX_LIST_PAGE_SIZE`, which is
     * exactly the shape `page-size-ceiling.test.ts` scan 1 exists to fail on. It
     * passed only because scan 1 filtered lines on the literal string
     * `page_size` and this parameter is spelled `per_page`; a working check
     * whose scope is narrower than its subject. The scan now reads all three
     * spellings.
     *
     * The consequence was live, not hypothetical: this schema is `.parse()`d
     * without `.strict()` (`rights-requests/routes.ts`, `controller.ts`), so zod
     * STRIPS a client-sent `page_size` and applies `per_page`'s default of 25 —
     * which is why `ParentRecordPicker.tsx` was rendering 25 rights-request rows
     * while asking for 500.
     */
    per_page: z.coerce.number().int().min(1).max(MAX_LIST_PAGE_SIZE).default(25),
    sort: z
        .string()
        .regex(/^[a-z_]+:(asc|desc)$/)
        .default('created_at:desc'),
})
    /*
     * fix-sprint-20260925-1400 F25 (idea-catalog #208) — strict. A key this
     * schema does not declare is refused (400) rather than stripped: a stripped
     * `page_size` returned the default 25 rows to a caller that asked for 500,
     * with no error. Callers proven first (2026-10-20): `TicketListPage`
     * (list and bulk export), `DataSubjectDetailPage`, `DsrIntegrationPage`,
     * `ParentRecordPicker` and the two bulk-export e2e specs send only declared
     * keys.
     */
    .strict();
//# sourceMappingURL=case.js.map